import { i18n } from '../i18n.js';
import { onLanguageChange } from '../i18n-events.js';
/**
 * fullscreen-toggle.js
 * The outermost control of the top-right chrome rail: one compact glass
 * trigger, cut from the same cloth as the audio trigger beside it, that puts
 * the whole document into full screen and takes it back out.
 *
 * It renders ONLY where the Fullscreen API actually exists. That is not
 * defensive habit: iOS Safari on iPhone has no element-level fullscreen at all
 * (which is why index.html ships the Add-to-Home-Screen meta tags instead), so
 * an unconditional button would be a dead control for every iPhone player.
 * When the capability is missing the trigger is absent and `body` never gains
 * `fullscreen-capable`, which is what collapses the rail's reserved slot back
 * to zero — see `--mf-rail-fullscreen-slot` in main.css.
 *
 * The label is read back from the document after every attempt rather than
 * assumed from the press, so a rejected request (permissions policy, a denied
 * user activation) leaves the control truthfully reading "Full screen".
 */

let FullscreenToggle = {};

FullscreenToggle = (function () {
    const ID = 'fullscreen-toggle';
    const CAPABLE_CLASS = 'fullscreen-capable';


    // Four corner brackets, pointing out to enter and in to leave. Drawn rather
    // than typed: the glyphs that mean this (⛶, ⤢) render as anything from a
    // box to tofu depending on the installed fonts.
    const ENTER_PATH = 'M9 4H5a1 1 0 0 0-1 1v4a1 1 0 0 0 2 0V6h3a1 1 0 0 0 0-2Zm6 0h4a1 1 0 0 1 1 1v4a1 1 0 0 1-2 0V6h-3a1 1 0 0 1 0-2ZM5 14a1 1 0 0 1 1 1v3h3a1 1 0 0 1 0 2H5a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1Zm14 0a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-4a1 1 0 0 1 0-2h3v-3a1 1 0 0 1 1-1Z';
    const EXIT_PATH = 'M9 4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H5a1 1 0 0 1 0-2h3V5a1 1 0 0 1 1-1Zm6 0a1 1 0 0 1 1 1v3h3a1 1 0 0 1 0 2h-4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1ZM4 15a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-2 0v-3H5a1 1 0 0 1-1-1Zm10 0a1 1 0 0 1 1-1h4a1 1 0 0 1 0 2h-3v3a1 1 0 0 1-2 0v-4Z';

    /** @type {HTMLButtonElement|null} */
    let button = null;
    let wired = false;
    let pending = false;

    /**
     * Read a member TypeScript's lib.dom does not declare — the `webkit`-
     * prefixed half of the Fullscreen API, which desktop Safari still ships as
     * its only spelling.
     * @param {object} host
     * @param {string} name
     */
    function vendor(host, name) {
        return /** @type {Record<string, unknown>} */ (host)[name];
    }

    function t(key, fallback) {
        const value = i18n?.t?.(key);
        return (typeof value === 'string' && value && value !== key) ? value : fallback;
    }

    function bound(host, ...names) {
        for (const name of names) {
            if (typeof host?.[name] === 'function') return host[name].bind(host);
        }
        return null;
    }

    function enterFn() {
        const root = document.documentElement;
        return bound(root, 'requestFullscreen', 'webkitRequestFullscreen');
    }

    function leaveFn() {
        return bound(document, 'exitFullscreen', 'webkitExitFullscreen');
    }

    // `fullscreenEnabled` is the permission half — it is false inside an iframe
    // that was not granted `allowfullscreen`, even though the method exists.
    function isSupported() {
        const permitted = document.fullscreenEnabled === true
            || vendor(document, 'webkitFullscreenEnabled') === true;
        return permitted && !!enterFn() && !!leaveFn();
    }


    function isFullscreen() {
        return !!(document.fullscreenElement || vendor(document, 'webkitFullscreenElement'));
    }

    /**
     * THE GESTURE HAS NOWHERE ELSE TO ANNOUNCE ITSELF.
     *
     * `fullscreen-gesture.js` also enters full screen on a double-press
     * anywhere inert, and a shortcut nobody knows about is a shortcut nobody
     * uses — so the tooltip of the control it duplicates is where it is named.
     * Only while the document is OUT of full screen: the gesture is one way in,
     * so naming it on the way out would promise an exit it does not perform.
     * The `aria-label` deliberately stays the bare action: a screen-reader user
     * is told what the button does, not how a pointer shortcut for it works.
     */
    function gestureHint() {
        let coarse = false;
        try {
            coarse = window.matchMedia?.('(hover: none), (pointer: coarse)')?.matches === true;
        } catch (_) { /* no matchMedia: assume the pointer that has tooltips */ }
        return coarse
            ? t('fullscreen.hintTap', 'Double-tap anywhere')
            : t('fullscreen.hintClick', 'Double-click anywhere');
    }

    function refresh() {
        if (!button) return;
        const active = isFullscreen();
        const label = active
            ? t('fullscreen.exit', 'Exit full screen')
            : t('fullscreen.enter', 'Full screen');
        button.dataset.state = active ? 'exit' : 'enter';
        button.setAttribute('aria-label', label);
        button.title = active ? label : `${label} · ${gestureHint()}`;
    }

    async function request(run) {
        // A request in flight has not moved `document.fullscreenElement` yet, so
        // a second press would read "not full screen" and ask again — which is
        // how a double-tap enters and immediately leaves.
        if (pending || !run) return;
        pending = true;
        try {
            // Old WebKit returns undefined here rather than a promise; awaiting
            // it is harmless and keeps one path for both.
            await run();
        } catch (_) {
            /* Denied or dismissed. The read-back below is the honest answer. */
        } finally {
            pending = false;
        }
        refresh();
    }

    /** The button's own press: in when out, out when in. */
    function toggle() {
        return request(isFullscreen() ? leaveFn() : enterFn());
    }

    /**
     * ONE WAY IN — what the double-press gesture calls.
     *
     * A gesture that also left full screen undid a choice the player had made
     * whenever a stray pair landed on the felt mid-match, and reflowed the
     * board under their cards. Leaving is Esc (which the browser announces on
     * every entry), Android's Back, or this button. Already in: nothing.
     */
    function enter() {
        if (isFullscreen()) return Promise.resolve();
        return request(enterFn());
    }

    function glyph() {
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('class', 'fullscreen-toggle-glyph');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
        for (const [state, d] of [['enter', ENTER_PATH], ['exit', EXIT_PATH]]) {
            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('class', `fullscreen-toggle-path fullscreen-toggle-path--${state}`);
            path.setAttribute('fill', 'currentColor');
            path.setAttribute('d', d);
            svg.appendChild(path);
        }
        return svg;
    }

    /* NO SEATING LIVES HERE ANY MORE.

       This control used to be a free-floating corner button, and it carried a
       measured battle seat to survive there: the enemy summoner's portrait
       reaches into the top-right and the mulligan countdown shares it, so the
       trigger read its own resting box, lifted above the portrait, and stepped
       inboard when a square screen left no room above it. Roughly eighty lines,
       plus a rail observer, a mulligan observer and a reseat scheduler.

       All of it is deleted because the button is no longer in the corner: it is
       a row inside the chrome menu, and the menu owns the one seat. Whatever
       the corner does to the menu, this moves with it for free. The capability
       gate, the click handler and the read-back-after-every-attempt label are
       the parts that mattered and they are all still here.
    */

    function watch() {
        if (wired) return;
        wired = true;
        document.addEventListener('fullscreenchange', refresh);
        document.addEventListener('webkitfullscreenchange', refresh);
        /* The i18n-events registry, NOT a `moonfall:language-changed` DOM
           event: nothing in the app dispatches that one, so a listener for it
           relabels nothing. This module's label also cannot be a `data-i18n`
           attribute, because which of the two keys applies depends on the
           document's current state. */
        onLanguageChange(refresh);

    }

    function build() {
        if (button?.isConnected) return button;
        // A framed document builds the trigger like any other; `isSupported()`
        // already answers for a frame that was not granted `allowfullscreen`.
        if (!isSupported() || !document.body) return null;

        // Own the element outright rather than adopting one already in the
        // document: adopting binds a SECOND click handler to a node that
        // already has one, and a toggle bound twice enters full screen and
        // leaves again in the same press.
        document.getElementById(ID)?.remove();
        button = document.createElement('button');
        button.id = ID;
        button.className = 'fullscreen-toggle';
        button.type = 'button';
        button.appendChild(glyph());
        button.addEventListener('click', toggle);
        /* Parked on `body` only until chrome-menu.js adopts it into the panel.
           Appending it here rather than reaching into the menu keeps the two
           modules independent of each other's load order: whichever runs second
           completes the arrangement. */
        document.body.appendChild(button);
        document.body.classList.add(CAPABLE_CLASS);

        watch();
        refresh();
        return button;
    }

    document.addEventListener('DOMContentLoaded', build);
    build();

    return {
        build,
        refresh,
        toggle,
        enter,
        isSupported,
        isFullscreen,
        element: () => button,
    };
})();

export { FullscreenToggle };
